import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  FileSpreadsheet,
  Image,
  Download,
  Trash2,
  Search,
  MoreHorizontal,
  Grid,
  List,
  File,
  Sparkles,
  Loader2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  PieChart,
  BarChart3,
  AlertCircle,
  CheckCircle2,
  X,
  Save,
  Tag,
  HelpCircle,
  Edit2,
  Eye,
  Globe
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";

import { API_URL } from "@/utils/api";

// Currency options with conversion rates (to USD)
const CURRENCIES = [
  { code: "USD", symbol: "$", name: "US Dollar", rate: 1 },
  { code: "EUR", symbol: "€", name: "Euro", rate: 1.08 },
  { code: "GBP", symbol: "£", name: "British Pound", rate: 1.27 },
  { code: "INR", symbol: "₹", name: "Indian Rupee", rate: 0.012 },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar", rate: 0.74 },
  { code: "AUD", symbol: "A$", name: "Australian Dollar", rate: 0.66 },
  { code: "JPY", symbol: "¥", name: "Japanese Yen", rate: 0.0067 },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan", rate: 0.14 },
  { code: "CHF", symbol: "Fr", name: "Swiss Franc", rate: 1.13 },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham", rate: 0.27 },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar", rate: 0.74 },
  { code: "MXN", symbol: "$", name: "Mexican Peso", rate: 0.058 },
  { code: "BRL", symbol: "R$", name: "Brazilian Real", rate: 0.20 },
];

// Document categories for uploads
const DOCUMENT_CATEGORIES = [
  { value: "invoices", label: "Invoices" },
  { value: "company_documents", label: "Company Documents" },
  { value: "compliance_documents", label: "Compliance Documents" },
  { value: "past_returns", label: "Past Returns" },
  { value: "bank_statements", label: "Bank Statements" },
  { value: "credit_card_statements", label: "Credit Card Statements" },
  { value: "expense_sheets", label: "Expense Sheets" },
  { value: "other", label: "Other" }
];

// Predefined categories for tagging
const INCOME_CATEGORIES = [
  "Sales Revenue", "Service Income", "Interest Income", "Investment Income",
  "Rental Income", "Commission", "Refunds", "Other Income"
];

const EXPENSE_CATEGORIES = [
  "Payroll", "Rent", "Utilities", "Marketing", "Software/Subscriptions",
  "Professional Services", "Office Supplies", "Travel", "Insurance",
  "Equipment", "Inventory", "Bank Fees", "Taxes", "Other Expense"
];

const ALL_CATEGORIES = ["Suspense", ...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES];

// File type icons
const FILE_ICONS = {
  pdf: FileText,
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
  csv: FileSpreadsheet,
  png: Image,
  jpg: Image,
  jpeg: Image,
  heic: Image,
  heif: Image
};

export default function DocumentsPage({ user }) {
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");
  
  // Upload with category
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState("other");
  const [uploadDescription, setUploadDescription] = useState("");
  
  // AI Analysis state
  const [analyzing, setAnalyzing] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisDocument, setAnalysisDocument] = useState(null);
  const [editedTransactions, setEditedTransactions] = useState([]);
  const [savingTags, setSavingTags] = useState(false);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState("summary");
  
  // Document preview state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  
  // Currency conversion state
  const [sourceCurrency, setSourceCurrency] = useState("USD");
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [showCurrencySelect, setShowCurrencySelect] = useState(false);

  useEffect(() => {
    fetchDocuments();
    fetchDocumentRequests();
  }, []);

  const fetchDocuments = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const res = await fetch(`${API_URL}/api/documents/user`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentRequests = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const res = await fetch(`${API_URL}/api/user/document-requests`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setDocumentRequests(data);
      }
    } catch (err) {
      console.error("Error fetching document requests:", err);
    }
  };

  const handleFilesSelected = (files) => {
    if (!files || files.length === 0) return;
    setSelectedFiles(Array.from(files));
    setShowUploadModal(true);
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    const token = localStorage.getItem("auth_token");
    setUploading(true);
    setUploadProgress(0);
    
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", uploadCategory);
      formData.append("description", uploadDescription);
      
      try {
        const res = await fetch(`${API_URL}/api/documents/upload`, {
          method: "POST",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData
        });
        
        if (res.ok) {
          setUploadProgress(((i + 1) / selectedFiles.length) * 100);
          toast.success(`Uploaded: ${file.name}`);
        } else {
          const error = await res.json();
          toast.error(error.detail || `Failed to upload ${file.name}`);
        }
      } catch (err) {
        toast.error(`Error uploading ${file.name}`);
      }
    }
    
    setUploading(false);
    setUploadProgress(0);
    setShowUploadModal(false);
    setSelectedFiles([]);
    setUploadCategory("other");
    setUploadDescription("");
    fetchDocuments();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFilesSelected(files);
  };

  // Currency conversion helper
  const convertCurrency = (amount, fromCurrency, toCurrency = "USD") => {
    const fromRate = CURRENCIES.find(c => c.code === fromCurrency)?.rate || 1;
    const toRate = CURRENCIES.find(c => c.code === toCurrency)?.rate || 1;
    // Convert to USD first, then to target currency
    const amountInUSD = amount * fromRate;
    return amountInUSD / toRate;
  };

  const formatCurrency = (amount, currencyCode = "USD") => {
    const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];
    return `${currency.symbol}${Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get converted analysis results
  const getConvertedAnalysis = () => {
    if (!analysisResult) return null;
    if (sourceCurrency === displayCurrency) return analysisResult;
    
    const convertAmount = (amt) => convertCurrency(amt, sourceCurrency, displayCurrency);
    
    return {
      ...analysisResult,
      summary: analysisResult.summary ? {
        ...analysisResult.summary,
        total_income: convertAmount(analysisResult.summary.total_income || 0),
        total_expenses: convertAmount(analysisResult.summary.total_expenses || 0),
        net_income: convertAmount(analysisResult.summary.net_income || 0),
        average_transaction: convertAmount(analysisResult.summary.average_transaction || 0),
      } : null,
      transactions: (analysisResult.transactions || []).map(t => ({
        ...t,
        amount: convertAmount(t.amount || 0),
        original_amount: t.amount,
        original_currency: sourceCurrency
      })),
      category_breakdown: Object.fromEntries(
        Object.entries(analysisResult.category_breakdown || {}).map(([cat, data]) => [
          cat,
          typeof data === 'object' ? {
            ...data,
            total: convertAmount(data.total || 0),
            income: convertAmount(data.income || 0),
            expenses: convertAmount(data.expenses || 0),
          } : convertAmount(data)
        ])
      )
    };
  };

  // Document preview handler
  const handlePreviewDocument = (doc) => {
    setPreviewDocument(doc);
    setShowPreviewModal(true);
  };

  const getPreviewUrl = (doc) => {
    const token = localStorage.getItem("auth_token");
    // Use /api/documents/view/ which supports ?token= query param for iframe authentication
    return `${API_URL}/api/documents/view/${doc.document_id}?token=${token}`;
  };

  const canPreview = (fileType) => {
    return ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileType?.toLowerCase());
  };

  const handleDownload = async (doc) => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/documents/download/${doc.document_id}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.original_filename || doc.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Document downloaded!");
      } else {
        toast.error("Failed to download document");
      }
    } catch (err) {
      toast.error("Error downloading document");
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm("Delete this document?")) return;
    
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        toast.success("Document deleted");
        setDocuments(documents.filter(d => d.document_id !== docId));
      } else {
        toast.error("Failed to delete document");
      }
    } catch (err) {
      toast.error("Error deleting document");
    }
  };

  // AI Document Analysis
  const handleAnalyzeDocument = async (doc) => {
    if (!["pdf", "csv", "txt"].includes(doc.file_type)) {
      toast.error("AI analysis is only available for PDF, CSV, and TXT files");
      return;
    }

    setAnalyzing(doc.document_id);
    setAnalysisDocument(doc);
    
    const token = localStorage.getItem("auth_token");
    
    try {
      // First check if analysis already exists
      const existingRes = await fetch(`${API_URL}/api/documents/analysis/${doc.document_id}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (existingRes.ok) {
        const existing = await existingRes.json();
        setAnalysisResult(existing.analysis);
        setEditedTransactions(existing.analysis.transactions || []);
        setShowAnalysisModal(true);
        setAnalyzing(null);
        return;
      }
      
      // Run new analysis
      const res = await fetch(`${API_URL}/api/documents/analyze/${doc.document_id}`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        const result = await res.json();
        setAnalysisResult(result.analysis);
        // Initialize edited transactions with analysis results
        const txns = (result.analysis.transactions || []).map((txn, idx) => ({
          ...txn,
          id: idx,
          is_suspense: txn.category === "Suspense" || !txn.category
        }));
        setEditedTransactions(txns);
        setShowAnalysisModal(true);
        toast.success("Document analyzed successfully!");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Analysis failed");
      }
    } catch (err) {
      toast.error("Failed to analyze document");
      console.error(err);
    } finally {
      setAnalyzing(null);
    }
  };

  // Update transaction category
  const handleUpdateTransactionCategory = (index, newCategory) => {
    setEditedTransactions(prev => prev.map((txn, i) => 
      i === index ? { ...txn, category: newCategory, is_suspense: newCategory === "Suspense" } : txn
    ));
  };

  // Mark as suspense
  const handleToggleSuspense = (index) => {
    setEditedTransactions(prev => prev.map((txn, i) => 
      i === index ? { ...txn, is_suspense: !txn.is_suspense, category: !txn.is_suspense ? "Suspense" : txn.category } : txn
    ));
  };

  // Save tagged transactions
  const handleSaveTransactionTags = async () => {
    if (!analysisDocument) return;
    
    setSavingTags(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const res = await fetch(`${API_URL}/api/documents/analysis/${analysisDocument.document_id}/transactions`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ transactions: editedTransactions })
      });
      
      if (res.ok) {
        const result = await res.json();
        setAnalysisResult(result.analysis);
        toast.success("Tags saved successfully! Finance team will review.");
      } else {
        toast.error("Failed to save tags");
      }
    } catch (err) {
      toast.error("Error saving tags");
    } finally {
      setSavingTags(false);
    }
  };

  // Calculate live stats from edited transactions
  const getLiveStats = () => {
    let totalIncome = 0;
    let totalExpenses = 0;
    const incomeCategories = {};
    const expenseCategories = {};
    const suspenseItems = [];
    
    editedTransactions.forEach(txn => {
      // Apply currency conversion
      let amount = txn.amount || 0;
      if (sourceCurrency !== displayCurrency) {
        amount = convertCurrency(amount, sourceCurrency, displayCurrency);
      }
      const category = txn.category || "Uncategorized";
      
      if (txn.is_suspense || category === "Suspense") {
        suspenseItems.push({...txn, convertedAmount: amount});
      } else if (amount >= 0) {
        totalIncome += amount;
        incomeCategories[category] = (incomeCategories[category] || 0) + amount;
      } else {
        totalExpenses += Math.abs(amount);
        expenseCategories[category] = (expenseCategories[category] || 0) + Math.abs(amount);
      }
    });
    
    return {
      totalIncome,
      totalExpenses,
      netCashflow: totalIncome - totalExpenses,
      incomeCategories,
      expenseCategories,
      suspenseItems,
      currency: displayCurrency
    };
  };

  const getFileIcon = (fileType) => {
    const Icon = FILE_ICONS[fileType] || File;
    return Icon;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Format currency with display currency symbol
  const formatDisplayCurrency = (amount) => {
    const currency = CURRENCIES.find(c => c.code === displayCurrency) || CURRENCIES[0];
    const absAmount = Math.abs(amount || 0);
    const formatted = absAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${currency.symbol}${formatted}`;
  };

  const filteredDocs = documents.filter(doc =>
    (doc.original_filename || doc.filename || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (doc.category || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: documents.length,
    pdfs: documents.filter(d => d.file_type === "pdf").length,
    spreadsheets: documents.filter(d => ["xlsx", "xls", "csv"].includes(d.file_type)).length,
    images: documents.filter(d => ["png", "jpg", "jpeg", "heic", "heif"].includes(d.file_type)).length,
  };

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#121212] font-['Manrope']">Documents</h1>
            <p className="text-[#121212]/60">Upload and manage your financial documents</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setViewMode(viewMode === "list" ? "grid" : "list")}
              className="rounded-xl border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"
            >
              {viewMode === "list" ? <Grid className="w-4 h-4" /> : <List className="w-4 h-4" />}
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-[#121212] hover:bg-[#2a2a2a] gap-2 rounded-full px-6 font-semibold"
              data-testid="upload-document-btn"
            >
              <Upload className="w-4 h-4" />
              Upload
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
            className="hidden"
            onChange={(e) => handleFilesSelected(Array.from(e.target.files))}
          />
        </div>

        {/* Document Requests Alert */}
        {documentRequests.length > 0 && (
          <Card className="border-2 border-amber-300 bg-amber-50 rounded-2xl">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold text-amber-800 font-['Manrope']">Document Requests</p>
                  <p className="text-sm text-amber-700 mt-1">The finance team has requested the following documents:</p>
                  <div className="mt-3 space-y-2">
                    {documentRequests.map((req) => (
                      <div key={req.request_id} className="flex items-center justify-between bg-white p-3 rounded-xl">
                        <div>
                          <p className="font-medium text-[#121212]">{req.title}</p>
                          <p className="text-sm text-[#121212]/60">{req.category?.replace('_', ' ')}</p>
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full"
                          onClick={() => {
                            setUploadCategory(req.category);
                            setUploadDescription(req.title);
                            fileInputRef.current?.click();
                          }}
                        >
                          Upload Now
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl hover:border-[#BFFF00] transition-colors">
            <CardContent className="pt-4">
              <p className="text-sm text-[#121212]/60">Total Documents</p>
              <p className="text-2xl font-bold text-[#121212] mt-1 font-['Manrope']">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-red-200 rounded-2xl hover:border-red-400 transition-colors">
            <CardContent className="pt-4">
              <p className="text-sm text-[#121212]/60">PDFs</p>
              <p className="text-2xl font-bold text-red-600 mt-1 font-['Manrope']">{stats.pdfs}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-green-200 rounded-2xl hover:border-green-400 transition-colors">
            <CardContent className="pt-4">
              <p className="text-sm text-[#121212]/60">Spreadsheets</p>
              <p className="text-2xl font-bold text-green-600 mt-1 font-['Manrope']">{stats.spreadsheets}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-[#BFFF00]/50 rounded-2xl hover:border-[#BFFF00] transition-colors">
            <CardContent className="pt-4">
              <p className="text-sm text-[#121212]/60">Images</p>
              <p className="text-2xl font-bold text-[#121212] mt-1 font-['Manrope']">{stats.images}</p>
            </CardContent>
          </Card>
        </div>

        {/* Upload Area */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${
            dragOver ? "border-[#BFFF00] bg-[#F7FFE5]" : "border-[#BFFF00]/30 bg-[#F7FFE5]/30"
          }`}
        >
          {uploading ? (
            <div className="space-y-3">
              <Loader2 className="w-10 h-10 text-[#121212] animate-spin mx-auto" />
              <p className="text-[#121212]/60">Uploading documents...</p>
              <Progress value={uploadProgress} className="w-64 mx-auto" />
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-[#121212]/40 mx-auto mb-3" />
              <p className="font-bold text-[#121212] font-['Manrope']">Drop files here or click to upload</p>
              <p className="text-sm text-[#121212]/60 mt-1">PDF, Excel, CSV, PNG, JPEG, HEIC</p>
              <Button
                variant="outline"
                className="mt-4 rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"
                onClick={() => fileInputRef.current?.click()}
              >
                Browse Files
              </Button>
            </>
          )}
        </div>

        {/* AI Analysis Banner */}
        <Card className="border-2 border-dashed border-[#BFFF00] bg-gradient-to-r from-[#F7FFE5] to-[#EDFFC2] rounded-2xl">
          <CardContent className="py-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#121212]" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-[#121212] font-['Manrope']">AI-Powered Document Analysis</p>
              <p className="text-sm text-[#121212]/60">
                Upload bank statements or financial documents and let AI extract transactions and generate cash flow reports
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Documents List */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-[#121212] font-['Manrope']">All Documents</h2>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#121212]/40" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-[#F7FFE5] border-[#BFFF00]/30 rounded-xl focus:ring-2 focus:ring-[#BFFF00]/50"
              />
            </div>
          </div>


          {filteredDocs.length === 0 ? (
            <Card className="border border-gray-200">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="font-medium text-gray-900">No documents yet</p>
                <p className="text-sm text-gray-500 mt-1">Upload your first document to get started</p>
              </CardContent>
            </Card>
          ) : viewMode === "list" ? (
            <Card className="border border-gray-200 overflow-hidden">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Name</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Category</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Type</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Size</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Uploaded</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredDocs.map((doc) => {
                      const Icon = getFileIcon(doc.file_type);
                      const canAnalyze = ["pdf", "csv", "txt"].includes(doc.file_type);
                      const categoryLabel = DOCUMENT_CATEGORIES.find(c => c.value === doc.category)?.label || doc.category || "Other";
                      return (
                        <tr key={doc.document_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                                <Icon className="w-4 h-4 text-gray-500" />
                              </div>
                              <span className="font-medium text-gray-900 truncate max-w-[200px]">
                                {doc.original_filename || doc.filename}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs capitalize bg-[#F7FFE5] border-[#BFFF00]/30">
                              {categoryLabel}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="uppercase text-xs">
                              {doc.file_type}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-sm">
                            {formatFileSize(doc.file_size)}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-sm">
                            {new Date(doc.uploaded_at || doc.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {canAnalyze && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAnalyzeDocument(doc)}
                                  disabled={analyzing === doc.document_id}
                                  className="gap-1 text-[#2563eb]"
                                  data-testid={`analyze-doc-${doc.document_id}`}
                                >
                                  {analyzing === doc.document_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-4 h-4" />
                                  )}
                                  Analyze
                                </Button>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {canPreview(doc.file_type) && (
                                    <DropdownMenuItem onClick={() => handlePreviewDocument(doc)}>
                                      <Eye className="w-4 h-4 mr-2" /> Quick View
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => handleDownload(doc)}>
                                    <Download className="w-4 h-4 mr-2" /> Download
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDelete(doc.document_id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredDocs.map((doc) => {
                const Icon = getFileIcon(doc.file_type);
                const canAnalyze = ["pdf", "csv", "txt"].includes(doc.file_type);
                return (
                  <Card key={doc.document_id} className="border border-gray-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-gray-500" />
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canPreview(doc.file_type) && (
                              <DropdownMenuItem onClick={() => handlePreviewDocument(doc)}>
                                <Eye className="w-4 h-4 mr-2" /> Quick View
                              </DropdownMenuItem>
                            )}
                            {canAnalyze && (
                              <DropdownMenuItem onClick={() => handleAnalyzeDocument(doc)}>
                                <Sparkles className="w-4 h-4 mr-2 text-[#2563eb]" /> AI Analyze
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDownload(doc)}>
                              <Download className="w-4 h-4 mr-2" /> Download
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(doc.document_id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <p className="font-medium text-gray-900 truncate text-sm">{doc.filename}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="uppercase text-xs">{doc.file_type}</Badge>
                        <span className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* AI Analysis Results Modal - Enhanced */}
      <Dialog open={showAnalysisModal} onOpenChange={setShowAnalysisModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#BFFF00]" />
              AI Document Analysis
              {analysisResult?.user_reviewed && (
                <Badge className="bg-green-100 text-green-700 ml-2">Reviewed</Badge>
              )}
            </DialogTitle>
            {/* Currency Selection */}
            <div className="flex items-center gap-4 mt-2 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Source Currency:</span>
                <Select value={sourceCurrency} onValueChange={setSourceCurrency}>
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Display in:</span>
                <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {sourceCurrency !== displayCurrency && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Converted from {sourceCurrency}
                </Badge>
              )}
            </div>
          </DialogHeader>
          
          {analysisResult && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Document Info */}
              <div className="flex items-center gap-3 p-3 bg-[#F7FFE5] rounded-lg mb-4 border border-[#BFFF00]/30">
                <FileText className="w-5 h-5 text-[#121212]" />
                <div className="flex-1">
                  <p className="font-medium text-sm">{analysisDocument?.filename}</p>
                  <p className="text-xs text-[#121212]/60">
                    {analysisResult.document_type || "Financial Document"}
                  </p>
                </div>
                <Button
                  onClick={handleSaveTransactionTags}
                  disabled={savingTags}
                  className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full"
                  data-testid="save-tags-btn"
                >
                  {savingTags ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Tags
                </Button>
              </div>

              {/* Tabs */}
              <Tabs value={activeAnalysisTab} onValueChange={setActiveAnalysisTab} className="flex-1 overflow-hidden flex flex-col">
                <TabsList className="bg-[#F7FFE5] border border-[#BFFF00]/30 rounded-xl p-1">
                  <TabsTrigger value="summary" className="rounded-lg data-[state=active]:bg-[#BFFF00] data-[state=active]:text-[#121212]">Summary</TabsTrigger>
                  <TabsTrigger value="transactions" className="rounded-lg data-[state=active]:bg-[#BFFF00] data-[state=active]:text-[#121212]">
                    Transactions ({editedTransactions.length})
                  </TabsTrigger>
                  <TabsTrigger value="suspense" className="rounded-lg data-[state=active]:bg-[#BFFF00] data-[state=active]:text-[#121212]">
                    Suspense ({getLiveStats().suspenseItems.length})
                  </TabsTrigger>
                  <TabsTrigger value="categories" className="rounded-lg data-[state=active]:bg-[#BFFF00] data-[state=active]:text-[#121212]">Categories</TabsTrigger>
                </TabsList>

                {/* Summary Tab */}
                <TabsContent value="summary" className="flex-1 overflow-y-auto mt-4 space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="border-2 border-dashed border-green-300 bg-green-50 rounded-2xl">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-green-600" />
                          <p className="text-sm text-green-700 font-medium">Total Income</p>
                        </div>
                        <p className="text-2xl font-bold text-green-700 font-['Manrope']">
                          {formatDisplayCurrency(getLiveStats().totalIncome)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-dashed border-red-300 bg-red-50 rounded-2xl">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingDown className="w-4 h-4 text-red-600" />
                          <p className="text-sm text-red-700 font-medium">Total Expenses</p>
                        </div>
                        <p className="text-2xl font-bold text-red-700 font-['Manrope']">
                          {formatDisplayCurrency(getLiveStats().totalExpenses)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-2 border-dashed border-[#BFFF00]/50 bg-[#F7FFE5] rounded-2xl">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4 text-[#121212]" />
                          <p className="text-sm text-[#121212]/70 font-medium">Net Cash Flow</p>
                        </div>
                        <p className={`text-2xl font-bold font-['Manrope'] ${getLiveStats().netCashflow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {formatDisplayCurrency(getLiveStats().netCashflow)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Insights */}
                  {analysisResult.insights && analysisResult.insights.length > 0 && (
                    <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base font-bold flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-[#BFFF00]" />
                          Key Insights
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {analysisResult.insights.map((insight, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-[#121212]/80">
                              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {insight}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Suspense Warning */}
                  {getLiveStats().suspenseItems.length > 0 && (
                    <Card className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-2xl">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2">
                          <HelpCircle className="w-5 h-5 text-amber-600" />
                          <div>
                            <p className="font-medium text-amber-800">
                              {getLiveStats().suspenseItems.length} items need your review
                            </p>
                            <p className="text-sm text-amber-700">
                              Go to the Suspense tab to categorize these transactions
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Transactions Tab */}
                <TabsContent value="transactions" className="flex-1 overflow-hidden mt-4">
                  <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl h-full overflow-hidden">
                    <CardContent className="p-0 h-full overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F7FFE5] sticky top-0 z-10">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold text-[#121212]/70">Date</th>
                            <th className="text-left px-3 py-2 font-semibold text-[#121212]/70">Description</th>
                            <th className="text-left px-3 py-2 font-semibold text-[#121212]/70 w-48">Category</th>
                            <th className="text-right px-3 py-2 font-semibold text-[#121212]/70">Amount</th>
                            <th className="text-center px-3 py-2 font-semibold text-[#121212]/70 w-20">Suspense</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#BFFF00]/10">
                          {editedTransactions.map((txn, i) => (
                            <tr key={i} className={`hover:bg-[#F7FFE5]/50 ${txn.is_suspense ? 'bg-amber-50' : ''}`}>
                              <td className="px-3 py-2 text-[#121212]/70">{txn.date || "-"}</td>
                              <td className="px-3 py-2 text-[#121212] truncate max-w-[200px]" title={txn.description}>
                                {txn.description}
                              </td>
                              <td className="px-3 py-2">
                                <Select
                                  value={txn.category || "Suspense"}
                                  onValueChange={(val) => handleUpdateTransactionCategory(i, val)}
                                >
                                  <SelectTrigger className="h-8 text-xs rounded-lg border-[#BFFF00]/30">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Suspense" className="text-amber-600">
                                      <span className="flex items-center gap-1">
                                        <HelpCircle className="w-3 h-3" /> Suspense
                                      </span>
                                    </SelectItem>
                                    <div className="px-2 py-1 text-xs text-gray-500 font-semibold">Income</div>
                                    {INCOME_CATEGORIES.map(cat => (
                                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                    <div className="px-2 py-1 text-xs text-gray-500 font-semibold">Expense</div>
                                    {EXPENSE_CATEGORIES.map(cat => (
                                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className={`px-3 py-2 text-right font-medium ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatDisplayCurrency(sourceCurrency !== displayCurrency ? convertCurrency(txn.amount, sourceCurrency, displayCurrency) : txn.amount)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <button
                                  onClick={() => handleToggleSuspense(i)}
                                  className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                                    txn.is_suspense 
                                      ? 'bg-amber-100 border-amber-400 text-amber-600' 
                                      : 'border-gray-300 hover:border-[#BFFF00]'
                                  }`}
                                >
                                  {txn.is_suspense && <HelpCircle className="w-4 h-4" />}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Suspense Tab */}
                <TabsContent value="suspense" className="flex-1 overflow-hidden mt-4">
                  <Card className="border-2 border-dashed border-amber-300 bg-amber-50/50 rounded-2xl h-full overflow-hidden">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-bold flex items-center gap-2">
                        <HelpCircle className="w-5 h-5 text-amber-600" />
                        Suspense Items - Need Review
                      </CardTitle>
                      <p className="text-sm text-amber-700">
                        These transactions couldn't be automatically categorized. Please assign a category to help your finance team.
                      </p>
                    </CardHeader>
                    <CardContent className="overflow-y-auto max-h-[400px]">
                      {getLiveStats().suspenseItems.length === 0 ? (
                        <div className="text-center py-8">
                          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                          <p className="font-medium text-green-700">All transactions categorized!</p>
                          <p className="text-sm text-green-600">Great job - no suspense items remaining.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {getLiveStats().suspenseItems.map((txn, i) => {
                            const originalIndex = editedTransactions.findIndex(t => t === txn);
                            return (
                              <div key={i} className="p-3 bg-white rounded-xl border border-amber-200 flex items-center gap-4">
                                <div className="flex-1">
                                  <p className="font-medium text-[#121212]">{txn.description}</p>
                                  <div className="flex items-center gap-3 mt-1 text-sm text-[#121212]/60">
                                    <span>{txn.date || "No date"}</span>
                                    <span className={`font-medium ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {formatDisplayCurrency(txn.convertedAmount || txn.amount)}
                                    </span>
                                  </div>
                                </div>
                                <Select
                                  value={txn.category || "Suspense"}
                                  onValueChange={(val) => handleUpdateTransactionCategory(originalIndex, val)}
                                >
                                  <SelectTrigger className="w-48 h-9 rounded-lg border-amber-300">
                                    <SelectValue placeholder="Select category" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <div className="px-2 py-1 text-xs text-gray-500 font-semibold">Income</div>
                                    {INCOME_CATEGORIES.map(cat => (
                                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                    <div className="px-2 py-1 text-xs text-gray-500 font-semibold">Expense</div>
                                    {EXPENSE_CATEGORIES.map(cat => (
                                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Categories Tab */}
                <TabsContent value="categories" className="flex-1 overflow-y-auto mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Income Categories */}
                    <Card className="border-2 border-dashed border-green-300 rounded-2xl">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base font-bold flex items-center gap-2 text-green-700">
                          <TrendingUp className="w-4 h-4" />
                          Income Categories
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {Object.keys(getLiveStats().incomeCategories).length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No income categorized yet</p>
                        ) : (
                          <div className="space-y-2">
                            {Object.entries(getLiveStats().incomeCategories)
                              .sort((a, b) => b[1] - a[1])
                              .map(([category, amount]) => (
                                <div key={category} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                                  <span className="text-sm text-[#121212]">{category}</span>
                                  <span className="font-medium text-green-700">{formatDisplayCurrency(amount)}</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Expense Categories */}
                    <Card className="border-2 border-dashed border-red-300 rounded-2xl">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base font-bold flex items-center gap-2 text-red-700">
                          <TrendingDown className="w-4 h-4" />
                          Expense Categories
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {Object.keys(getLiveStats().expenseCategories).length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No expenses categorized yet</p>
                        ) : (
                          <div className="space-y-2">
                            {Object.entries(getLiveStats().expenseCategories)
                              .sort((a, b) => b[1] - a[1])
                              .map(([category, amount]) => (
                                <div key={category} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                                  <span className="text-sm text-[#121212]">{category}</span>
                                  <span className="font-medium text-red-700">{formatDisplayCurrency(amount)}</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Modal with Category Selection */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Manrope']">Upload Documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Selected Files */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#121212]/60">Selected Files</p>
              <div className="max-h-32 overflow-y-auto space-y-2">
                {selectedFiles.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-[#F7FFE5] rounded-lg">
                    <FileText className="w-4 h-4 text-[#121212]" />
                    <span className="text-sm text-[#121212] truncate flex-1">{file.name}</span>
                    <span className="text-xs text-[#121212]/50">{formatFileSize(file.size)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#121212]">Document Category *</label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="rounded-xl border-2 border-[#BFFF00]/30">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#121212]">Description (optional)</label>
              <Input
                placeholder="Add a description..."
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                className="rounded-xl border-2 border-[#BFFF00]/30"
              />
            </div>

            {uploading && (
              <div className="space-y-2">
                <p className="text-sm text-[#121212]/60">Uploading...</p>
                <Progress value={uploadProgress} className="w-full" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setSelectedFiles([]);
              }}
              className="rounded-full"
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFileUpload}
              disabled={uploading || selectedFiles.length === 0}
              className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload {selectedFiles.length} {selectedFiles.length === 1 ? "file" : "files"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {previewDocument && (
                <>
                  {(() => {
                    const Icon = getFileIcon(previewDocument.file_type);
                    return <Icon className="w-5 h-5 text-gray-500" />;
                  })()}
                  {previewDocument.original_filename || previewDocument.filename}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Quick preview - scroll to view full document
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-gray-100 rounded-lg overflow-hidden">
            {previewDocument && canPreview(previewDocument.file_type) ? (
              previewDocument.file_type === 'pdf' ? (
                <iframe
                  src={getPreviewUrl(previewDocument)}
                  className="w-full h-full min-h-[500px]"
                  style={{ border: 'none' }}
                  title={previewDocument.original_filename || previewDocument.filename}
                />
              ) : (
                <div className="w-full h-full overflow-auto p-4 flex items-center justify-center">
                  <img 
                    src={getPreviewUrl(previewDocument)} 
                    alt={previewDocument.original_filename || previewDocument.filename}
                    className="max-w-full h-auto object-contain"
                  />
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-[400px]">
                <File className="w-16 h-16 text-gray-400 mb-4" />
                <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                <Button onClick={() => previewDocument && handleDownload(previewDocument)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download File
                </Button>
              </div>
            )}
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setShowPreviewModal(false)} className="rounded-full">
              Close
            </Button>
            <Button 
              onClick={() => previewDocument && handleDownload(previewDocument)}
              className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
